import { BreakpointObserver } from '@angular/cdk/layout';
import { Location, isPlatformBrowser } from '@angular/common';
import { ApplicationRef, DestroyRef, Injectable, PLATFORM_ID, Type, ViewContainerRef, inject } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationExtras, NavigationStart, Params, Route, Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ConfirmationService, MenuItem, MessageService } from '@openng/optimus-ui/api';
import { Observer, timer } from 'rxjs';
import { ChangeSummary } from 'src/modules/enaio/netgen/ChangeSummary';
import { PrimeModule } from 'src/modules/prime/prime.module';
import { SmTableComponent } from 'src/modules/sm-base/components/sm-table/sm-table.component';
import { InsertionPointDirective } from 'src/modules/sm-base/directives/insertion-point.directive';
import { SmErrorHandler } from 'src/modules/sm-base/misc/sm-error-handler';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { Emitter } from 'src/modules/sm-base/models/emitter.model';
import { Listener } from 'src/modules/sm-base/models/listener.model';
import { PrimeMessageSeverity } from 'src/modules/sm-base/models/prime-message-severity.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { AuthenticationService } from 'src/modules/sm-base/services/authentication.service';
import { FeatureModuleService } from 'src/modules/sm-base/services/feature-module.service';
import { FileUploadService } from "src/modules/sm-base/services/file-upload.service";
import { MessageDialogService } from 'src/modules/sm-base/services/message-dialog.service';
import { BackendResponse } from 'src/modules/sm-base/shared/backend-response.model';
import { FrontendFieldError } from "src/modules/sm-base/netgen/FrontendFieldError";
import { SmBaseEntity } from 'src/modules/sm-base/shared/sm-base-entity.model';
import { GuiUtils, GuiUtilsInternal } from 'src/modules/utils/misc/gui-utils';
import { UserInteractionItem } from 'src/modules/utils/misc/user-interaction-item.model';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { ClassType } from 'src/modules/utils/shared/class-type';
import { Objects, ObjectsInternal } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { AppMainComponent } from '../components/app-main/app-main.component';
import { AppTab } from '../models/app-tab.model';
import { ComponentView } from '../models/component-view.model';
import { Config } from '../models/config.model';
import { SaveDataHandler } from '../models/save-data-handler.model';
import { SaveDataResult } from '../models/save-data-result.model';
import { UserNotificationType } from '../models/user-notification-type.model';
import { MainFrontendConfig } from '../netgen/MainFrontendConfig';
import { Subscription } from 'src/modules/sm-base/models/subscription.model';
import { SmTimer } from 'src/modules/sm-base/models/sm-timer.model';
import { ListenerCallback, ListenerCallbackInfo } from 'src/modules/sm-base/models/listener-callback-info.model';

@Injectable({
    providedIn: 'root'
})
export class MainAppService {

    //Das bentötigen wir, um aus AuthInterceptor auf den MainAppService zugreifen zu können - andernfalls gibt es bei der Initialisierung einen Dependency Injection Error
    static instance: MainAppService;

    appName: string;
    pageTitle: string;
    menuModel: AppTab[] = [];
    visibleMenuTabs: AppTab[] = [];
    language = "de";

    breadcrumbsVisible = false;
    showBreadcrumbs = true;
    breadcrumbsInToolBar = true;
    breadcrumbsHome: MenuItem = null;
    breadcrumbsItems: MenuItem[] = [];
    staticHeaderHtml: string;
    showSidebar = false;
    showTopbar = true;
    showSpinner = false;
    storedErrors: any[] = [];
    unreadNotificationCount = 0;
    debugRoutes = false;
    userInteractionHistory: UserInteractionItem[] = [];
    backendVersion = "";
    frontendVersionFromBackend = "";
    loadedDate: Date;
    lastUserInteractionDate: Date;
    mouseX = 0;
    mouseY = 0;
    compactGui = false;
    warningOnSwitchLanguage = true;
    allowDarkMode = false;
    reportErrorsToBackend = true;
    privacyPolicy = false;
    developer = false;
    advancedDeveloper = false;
    isBeta = false;

    toolbarBackButton = false;
    useNotifications = false;
    toolbarHomeButton = false;
    toolbarNotificationsButton = false;
    toolbarHelpButton = false;
    toolbarButtons: MenuItem[] = [];
    toolbarShowUser = true;
    multilingual = false;

    contextMenu: MenuItem[];
    contextMenuClass = "";

    changeDetectionCount = 0;
    darkMode: boolean = null;
    darkModeQuery: MediaQueryList;

    mainConfig: MainFrontendConfig = null;

    useFocusTracker = false;
    private focusTrackerOverlay: HTMLDivElement;

    private keyboardListeners: OrdinaryObject<(KeyboardEvent) => void> = {};
    private keyboardListenersByKey: OrdinaryObject<string[]> = {};
    onDarkModeChanged = new Emitter<boolean>();

    onSelectionCallback: () => void;

    private titleService = inject(Title);
    confirmationService = inject(ConfirmationService);

    private breakpointObserver: BreakpointObserver;
    private _isMobile = false;
    private developerErrorCaptureEnabled = false;

    async showConfirmPopup(message: string): Promise<boolean> {
        const el = AppMainComponent.instance.confirmPopupTargetRef.nativeElement as HTMLElement;
        el.style.left = this.mouseX + "px";
        el.style.top = this.mouseY + "px";

        return new Promise(resolve => {
            this.confirmationService.confirm({
            target: el,
            message,
            acceptLabel: 'Ja',
            rejectLabel: 'Nein',
            accept: () => resolve(true),
            reject: () => resolve(false),
            rejectVisible: true,
            dismissableMask: false
            });

            requestAnimationFrame(() => {
                const overlay = Array.from(document.querySelectorAll<HTMLElement>('.p-confirmpopup')).at(-1);
                if (overlay == null) {
                    return;
                }
                const w = overlay.offsetWidth;
                overlay.style.left = Math.max(0, overlay.offsetLeft - w) + 'px';
            });
        });
    }

    globalKeyHandler(event: KeyboardEvent): void {
        AppMainComponent.instance?.onKeyPress(event);
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private location: Location,
        private messageService: MessageService,
        public dialog: MessageDialogService,
        public fileUpload: FileUploadService,
        private applicationRef: ApplicationRef,
        public sanitizer: DomSanitizer,
        public auth: AuthenticationService,
        public featureModules: FeatureModuleService,
        public deviceDetector: DeviceDetectorService) {

        const platformId = inject(PLATFORM_ID);
        const destroyRef = inject(DestroyRef);

        if (isPlatformBrowser(platformId)) {
            // WICHTIG: { capture: true } = läuft VOR den Listenern von Optimus UI
            window.addEventListener('keydown', this.globalKeyHandler, { capture: true });

            destroyRef.onDestroy(() => {
                window.removeEventListener('keydown', this.globalKeyHandler, { capture: true });
            });
        }

        this.breakpointObserver = new BreakpointObserver();
        this.breakpointObserver.observe(['(max-width: 767px), (max-height: 500px)'])
        .subscribe(result => {
            let changed = this._isMobile != result.matches;
            this._isMobile = result.matches;
            if (changed) {
                void this.onIsMobileChanged.emit(this._isMobile);
            }
        });

        this.darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.darkModeQuery.addEventListener('change', () => this.updateDarkMode());
        this.updateDarkMode();

        GuiUtils.checkSingleton(this);
        MainAppService.instance = this;
        this.appName = "AWP Portal";
        this.updatePageTitle(this.appName);
        this.toolbarShowUser = true;
        this.loadedDate = new Date();
        this.language = localStorage.getItem("language") ?? "de";
        window.addEventListener('unhandledrejection',event => {
            SmErrorHandler.instance.handleError(event.reason);
        });

        this.router.events.subscribe(event => {
            if (this.debugRoutes) {
                console.group(`Router event: ${event.constructor.name}`);
                console.log(event);
                console.groupEnd();
            }
            if (event instanceof NavigationStart) {
                this.dialog.dialogService.dialogComponentRefMap.forEach((_, ref) => ref.close());

                if (this.mainConfig?.frontendVersionCheckAvailable && this.frontendVersionOutdated()) {
                    let lastUpdate = Utils.toNumber(localStorage.getItem("lastAppUpdate"), true, true, 0);
                    let time = new Date().getTime();
                    if (time - lastUpdate >= 60 * 60 * 1000) {
                        localStorage.setItem("lastAppUpdate", Objects.toString(time));
                        void RestEndpoint.main().post().body("Version wird aktualisiert: " + (window as any).frontendVersion + " zu " + this.frontendVersionFromBackend).run("api/app/log").getText().then(() => {
                            window.location.reload();
                        });
                    }
                    //this.showToast("warn", "Update", "Version veraltet. Bitte mit F5 neuladen");
                }

                this.userInteractionHistory.push(new UserInteractionItem(new Date(), "URL", [event.url, "Trigger: " + (event.navigationTrigger ?? "unbekannt")]));
            }
            else if (event instanceof NavigationEnd && event.urlAfterRedirects.startsWith("/base/")) {
                this.updatePageTitle(this.appName);
            }
        });

        timer(1_000, 60_000 * 5).subscribe(async _ => {
            await this.checkNotifications();
        });
    }


    boundUpdateFocusTrackerOverlay = this.updateFocusTrackerOverlay.bind(this);

    setUseFocusTracker(value: boolean): void {
        this.useFocusTracker = value;
        document.querySelector('#focus-tracker-overlay')?.remove();
         if (this.useFocusTracker) {

            this.focusTrackerOverlay = document.createElement('div');
            this.focusTrackerOverlay.id = 'focus-tracker-overlay';
            Object.assign(this.focusTrackerOverlay.style, {
                position: 'absolute',
                pointerEvents: 'none',
                border: '3px solid rgba(255, 0, 0, 0.6)',
                borderRadius: '4px',
                background: 'rgba(255, 0, 0, 0.1)',
                zIndex: 999999,
                transition: 'all 0.1s ease'
                });
            document.body.appendChild(this.focusTrackerOverlay);

            document.addEventListener('focusin', this.boundUpdateFocusTrackerOverlay, true);
            document.addEventListener('scroll', this.boundUpdateFocusTrackerOverlay, true);
            window.addEventListener('resize', this.boundUpdateFocusTrackerOverlay);
            this.updateFocusTrackerOverlay();
        }
        else {
            document.removeEventListener('focusin', this.boundUpdateFocusTrackerOverlay);
            document.removeEventListener('scroll', this.boundUpdateFocusTrackerOverlay);
            window.removeEventListener('resize', this.boundUpdateFocusTrackerOverlay);
        }
    }

    private updateFocusTrackerOverlay(): void {
        const el = document.activeElement;
        if (!el || el === document.body) {
            this.focusTrackerOverlay.style.display = 'none';
            return;
        }

        const rect = el.getBoundingClientRect();
        this.focusTrackerOverlay.style.display = 'block';
        this.focusTrackerOverlay.style.top = rect.top + window.scrollY + 'px';
        this.focusTrackerOverlay.style.left = rect.left + window.scrollX + 'px';
        this.focusTrackerOverlay.style.width = rect.width + 'px';
        this.focusTrackerOverlay.style.height = rect.height + 'px';
    }

    getLogoLoginPngUrl(): string {
        return this.getCiImageUrl("logo-login.png");
    }

    getLogoPngUrl(): string {
        return this.getCiImageUrl("logo.png");
    }

    private getCiImageUrl(fileName: string): string {
        return this.mainConfig?.ciFilesFromBackend ? RestEndpoint.getUrl() + "/api/app/ciFile?type=" + fileName : "assets/layout/images/" + fileName;
    }

    toggleDarkMode(alsoToggleAuto?: boolean): void {
        this.darkMode = alsoToggleAuto ? this.darkMode === true ? false : this.darkMode === false ? null : true : !this.isDarkMode();
        this.updateDarkMode();
    }

    isDarkMode(): boolean {
        return this.allowDarkMode && (this.darkMode ?? this.darkModeQuery.matches);
    }

    updateDarkMode(): void {
        this.onDarkModeChanged.emit(this.isDarkMode());
        if (this.isDarkMode()) {
            document.documentElement.classList.add("dark-mode");
        }
        else {
            document.documentElement.classList.remove("dark-mode");
        }
    }

    frontendVersionOutdated(): boolean {
        return !Utils.isNoe(this.frontendVersionFromBackend) && this.frontendVersionFromBackend != (window as any).frontendVersion;
    }

    updateMenuVisibility(): void {
        this.visibleMenuTabs = this.menuModel.filter(t => t.visible);
    }

    translation: OrdinaryObject = {};

    async setLanguage(value: string): Promise<void> {
        if (value == null || value != this.language) {
            let lng = value ?? this.language;
            this.translation = Objects.fromJson(Strings.def(await RestEndpoint.main().query({ language: lng }).run("api/app/translation").getText(), "{}"));
            PrimeModule.instance.setLanguage(lng);
            SmTableComponent.setLanguage(lng);
        }
        if (value != null) {
            this.language = value;
            localStorage.setItem("language", value);
        }
    }

    async checkNotifications(): Promise<void> {
        if (this.auth.isLoggedIn() && this.useNotifications) {
            this.unreadNotificationCount = await RestEndpoint.main().run("api/app/notification/unreadcount").getNumber();
        }
    }

    getUserName(): string {
        return this.auth.getUserName();
    }

    userInteracted(): void {
        this.lastUserInteractionDate = new Date();
    }

    userKeyPress(event: KeyboardEvent): string {
        this.userInteracted();

        let key = event.key;
        if (key == null) {
            return null;
        }

        if (event.altKey) {
            key = "Alt-" + key;
        }
        if (event.ctrlKey) {
            key = "Ctrl-" + key;
        }
        if (event.shiftKey) {
            key = "Shift-" + key;
        }
        key = key.toUpperCase();
        for (let guid of Objects.getSafe(this.keyboardListenersByKey, key, [])) {
            this.keyboardListeners[guid](event);
        }
        return key;
    }

    addKeyboardListener(key: string, callback: (KeyboardEvent) => void): string {
        key = key.toUpperCase();
        let guid = Utils.generateGuid();
        this.keyboardListeners[guid] = callback;
        if (!(key in this.keyboardListenersByKey)) {
            this.keyboardListenersByKey[key] = [];
        }
        this.keyboardListenersByKey[key] = [...this.keyboardListenersByKey[key], guid];
        return guid;
    }

    removeKeyboardListener(guid: string): void {
        delete this.keyboardListeners[guid];
        for (let key of Objects.getPropertyNames(this.keyboardListenersByKey)) {
            this.keyboardListenersByKey[key] = this.keyboardListenersByKey[key].filter(s => s != guid);
        }
    }

    //callback eigentlich vom Typ callback: (params: any) => any, das kollidiert aber mit eslint rule so-unsafe-argument
    subscribeToParamsChanges(route: ActivatedRoute, callback: any): void {
        route.params.subscribe(callback as Partial<Observer<Params>>);
    }

    //callback eigentlich vom Typ callback: (params: any) => any, das kollidiert aber mit eslint rule so-unsafe-argument
    subscribeToQueryParamsChanges(route: ActivatedRoute, callback: any): void {
        route.queryParams.subscribe(callback as Partial<Observer<Params>>);
    }

    handlePortalBackendErrorId(errorId: string): void {
        if (!Utils.isNoe(errorId)) {
            SmErrorHandler.instance.handleBackendError(errorId);
        }
    }

    backendVersionRetrieved(backendVersion: string, frontendVersionFromBackend: string): void {
        if (!Utils.isNoe(backendVersion)) {
            this.backendVersion = Strings.removePrefix(Strings.removeSuffix(backendVersion, "\""), "\"");
        }
        if (!Utils.isNoe(frontendVersionFromBackend)) {
            this.frontendVersionFromBackend = Strings.removePrefix(Strings.removeSuffix(frontendVersionFromBackend, "\""), "\"");
        }
    }

    async navigate(commands: any[], extras?: NavigationExtras): Promise<void> {
        await this.router.navigate(commands, extras);
    }

    async navigateChangeParams(queryParams: OrdinaryObject, replaceUrl = true, skipLocationChange = false): Promise<boolean> {
        return this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: "merge",
            replaceUrl,
            skipLocationChange
        });
    }

    navigateBack(): void {
        this.location.back();
    }

    reloadPage(): void {
        window.location.reload();
    }

    async navigateToUrl(url: string, replaceUrl?: boolean): Promise<void> {
        await this.router.navigateByUrl(url, { replaceUrl });
    }

    async navigateTo(routerLink: any[], replaceUrl?: boolean, queryParams?: Params): Promise<void> {
        await this.router.navigate(routerLink, { replaceUrl, queryParams });
    }

    async navigateToComponent(component: any, params?: OrdinaryObject, replaceUrl = false): Promise<void> {
        const routes = this.findRoutesForComponent(component);

        if (Utils.isNoe(routes)) {
            throw new Error("Für die Komponente " + component.name + " wurde keine Route gefunden");
        }

        let neededParams = params != null ? Objects.getPropertyNames(params) : [];
        for (let route of routes) {
            let routeParams = Strings.regexGetGroups(route.path, /:([a-zA-Z0-9]+)/g, 1);
            if (Arrays.equalsFlatUnordered(routeParams, neededParams)) {
                let path = route.path;
                for (let key of neededParams) {
                    path = path.replace(':' + key, encodeURIComponent(params[key] as string));
                }
                await this.router.navigate([path], {
                    replaceUrl
                });
                return;
            }
        }

        throw new Error("Für die Komponente " + component.name + " wurde keine passende Route für die angegebenen Parameter " + Objects.toJson(params) + " gefunden");
    }

    private findRoutesForComponent(component: any): Route[] {
        return this.flattenRoutes(this.router.config).filter(r => r.component === component);
    }

    private flattenRoutes(routes: Route[], basePath = ''): Route[] {
        return routes.flatMap(route => {
        const current = {
            ...route,
            path: basePath + (route.path ? '/' + route.path : '')
        };
        return route.children ?? (route as any)._loadedRoutes
            ? [current, ...this.flattenRoutes(route.children ?? (route as any)._loadedRoutes as Route[], current.path)]
            : [current];
        });
    }

    updateNavigation(pageTitle: string, breadcrumbsItems: MenuItem[]): void {
        GuiUtils.angularTimer(() => {
            this.updatePageTitle(pageTitle || (breadcrumbsItems.length == 0 ? "Home" : breadcrumbsItems[breadcrumbsItems.length - 1].label));
            this.updateBreadcrumbs(breadcrumbsItems);
        });
    }

    updateBreadcrumbs(breadcrumbsItems: MenuItem[]): void {
        this.breadcrumbsHome = { routerLink: ["/"], icon: 'fas fa-home' };
        this.breadcrumbsItems = Arrays.withoutNull(breadcrumbsItems);
        if (this.breadcrumbsItems.length > 0) {
            delete this.breadcrumbsItems[this.breadcrumbsItems.length - 1].routerLink;
        }
        this.breadcrumbsVisible = this.showBreadcrumbs;
        if (this.breadcrumbsItems.length == 0) {
            this.breadcrumbsItems = null;
        }
    }

    updatePageTitle(pageTitle: string): void {
        this.pageTitle = pageTitle;
        this.titleService.setTitle(pageTitle);
    }

    async updateQueryParams(key: string, value: any): Promise<void> {
        let queryParams: OrdinaryObject = {};
        queryParams[key] = Objects.toString(value);
        await this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    }

    getInstance(): this {
        return this;
    }

    createComponent<T>(parent: ViewContainerRef | InsertionPointDirective, type: Type<T>): T {
        let viewContainerRef: ViewContainerRef = parent instanceof InsertionPointDirective ? parent.viewContainerRef : parent;
        viewContainerRef.clear();
        return viewContainerRef.createComponent(type).instance;
    }

    clearToasts(isNotification = false, overrideService?: MessageService): void {
        (overrideService ?? this.messageService).clear(isNotification ? "notification": "top");
    }

    showInfoToast(detail: string, summary?: string, duration?: number, isNotification = false, overrideService?: MessageService): void {
        this.showToast("info", summary ?? this.t("general.info"), detail, duration, isNotification, overrideService);
    }

    showSuccessToast(detail: string, summary?: string, duration?: number, isNotification = false, overrideService?: MessageService): void {
        this.showToast("success", summary ?? "Erfolgreich", detail, duration, isNotification, overrideService);
    }

    showErrorToast(detail: string, summary?: string, duration?: number, isNotification = false, overrideService?: MessageService): void {
        this.showToast("error", summary ?? this.t("general.error"), detail, duration, isNotification, overrideService);
    }

    showToast(severity: PrimeMessageSeverity, summary: string, detail: string, duration?: number, isNotification = false, overrideService?: MessageService, messageId = "", onClose: (messageId: string) => void = null ): void {
        (overrideService ?? this.messageService).add({ severity, summary, detail, life: duration, key: isNotification ? "notification": "top", sticky: isNotification, data: {
            id: messageId,
            onClose
        } });
    }

    changeTheme(theme: string): void {
        this.replaceLink(document.getElementById('theme-css') as HTMLLinkElement, theme);
    }

    changeLayout(layout: string): void {
        this.replaceLink(document.getElementById('layout-css') as HTMLLinkElement, layout);
    }

    async copyToClipboardOrDialog(text: string, dialogTitle: string, copyToastText: string): Promise<void> {
        if (await GuiUtils.copyToClipboard(text)) {
            this.showSuccessToast(copyToastText, "Kopiert");
        }
        else {
            this.dialog.textArea(dialogTitle, text);
        }
    }

    isAdmin(): boolean {
        return this.auth.userDetails?.getDatabaseUser()?.isAdmin ?? false;
    }

    replaceLink(linkElement: HTMLLinkElement, href: string): void {
        const id = linkElement.getAttribute('id');
        const cloneLinkElement = linkElement.cloneNode(true) as HTMLLinkElement;

        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', id + '-clone');

        linkElement.parentNode.insertBefore(cloneLinkElement, linkElement.nextSibling);

        cloneLinkElement.addEventListener('load', () => {
            linkElement.remove();
            cloneLinkElement.setAttribute('id', id);
        });
    }

    getRouterDebugString(): string {
        return this.getRouterDebugStringInternal('', this.router.config);
    }

    triggerChangeDetection(): void {
        this.applicationRef.tick();
    }

    validateForm(form: FrontendForm, additionalValidation: () => FrontendFieldError[] = null, overrideService?: MessageService): boolean {
        let result = form.validate();
        if (additionalValidation != null) {
            result = [...result, ...additionalValidation()];
        }
        if (!Utils.isNoe(result)) {
            this.showErrorToast("Fehler in der Eingabe: \n\n" + Arrays.itemsToString(result, "\n\n", item => item.message), null, null, false, overrideService);
            return false;
        }
        return true;
    }

    openUrlInNewTab(url: string): boolean {
        let tab = window.open();
        if (tab != null) {
            tab.location.href = url;
            return true;
        }
        else {
            this.showErrorToast("Ein neuer Tab / ein neues Fenster konnte nicht geöffnet wurde. Möglicherweise wird dies durch Ihre Browsereinstellungen blockiert");
            return false;
        }
    }

    async actionHandler<T>(actionName: string, worker: () => Promise<T>, setBusy?: (busy: boolean) => void): Promise<{ ok: true; value: T } | { ok: false; value: null }> {
        try {
            setBusy?.(true);
            return {ok: true, value: await worker()};
        }
        catch (error) {
            const message = await this.getActionErrorMessage(error);
            const title = actionName + " – " + this.t("general.error");
            const lines = message.split(/\r?\n/);
            const summary = Strings.escapeHtml(lines[0]);
            const details = lines.slice(1).join("\n").trim();
            try {
                await this.dialog.info(summary, title, {
                    details,
                    detailsLabel: this.t("general.details", null, "Details", false)
                });
            }
            catch {
                //Falls bereits ein Dialog geöffnet ist, bleibt die Meldung als Benachrichtigung sichtbar.
                this.showErrorToast(message, title, null, true);
            }
            return {ok: false, value: null};
        }
        finally {
            setBusy?.(false);
        }
    }

    private async getActionErrorMessage(error: any): Promise<string> {
        const fallback = this.t("general.errorText", null, "Die Aktion konnte nicht ausgeführt werden.", false);
        const response = error?.response ?? error;
        if (response?.status === 0) {
            return this.t("general.backendUnreachable", null, "Das Backend ist nicht erreichbar. Bitte prüfen Sie die Verbindung.", false);
        }
        let payload = response?.error ?? response;
        try {
            if (payload instanceof Blob) {
                payload = await payload.text();
            }
            if (typeof payload == "string") {
                payload = Objects.fromJson(payload);
            }
            const backendResponse = payload != null && typeof payload == "object" ? BackendResponse.from(payload) : null;
            if (backendResponse != null) {
                const message = backendResponse.getMessage();
                if (!Utils.isNoe(message)) {
                    return message.trim();
                }
            }
        }
        catch {
            //Ungültige Fehlerantworten dürfen die ursprüngliche Fehlermeldung nicht überdecken.
        }
        const message = response?.message ?? error?.message;
        return typeof message == "string" && message.trim() != "" ? message.trim() :
            typeof error == "string" && error.trim() != "" ? error.trim() : fallback;
    }

    async genericDataHandler<T>(worker: () => Promise<T>, errorTitle?: string, onError?: "toast" | "dialog" | null): Promise<{ ok: boolean; value: T }> {
        try {
            return {ok: true, value: await worker()};
        }
        catch (ex) {
            if (onError != null) {
                let error = BackendResponse.getFromException(ex);
                if (onError == "dialog") {
                    await this.dialog.info(!Utils.isNoe(error.items) ? error.items[0].message : errorTitle, errorTitle);
                }
                else if (onError == "toast") {
                    this.showToast("error", errorTitle, !Utils.isNoe(error.items) ? error.items[0].message : errorTitle);
                }
            }
            return {ok: false, value: null};
        }
    }

    async loadDataHandler<T>(worker: () => Promise<T>, source: ComponentView): Promise<T> {
        try {
            return await worker();
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.loadDataError(!Utils.isNoe(error?.items) ? error.items[0].message : null, !Utils.isNoe(error?.items) && error.items[0].recoverable, source);
            throw ex;
        }
    }

    async loadDataError(message: string, recoverable: boolean, source: ComponentView): Promise<void> {
        let buttons = [new ButtonDefinition("back", "Zurück", false, "fas fa-backward")];
        if (recoverable) {
            buttons = [new ButtonDefinition("reload", "Erneut versuchen", false, "fas fa-redo-alt"), ...buttons];
        }
        let result = await this.dialog.show(message, "Fehler beim Laden der Daten", buttons);
        if (result == "reload") {
            source.ngOnInit();
        }
        else if (result == "back") {
            this.navigateBack();
        }
    }

    async getClipboardText(errorToast = true): Promise<string> {
        try {
            return await GuiUtils.fromClipboard();
        }
        catch (error: any) {
            if ((error.message.includes("Read permission denied") || error.message.includes("Clipboard read operation is not allowed.")) && errorToast) {
                this.showErrorToast("Der Zugriff auf die Zwischenablage wurde verweigert. Bitte erlauben Sie den Zugriff in Ihren Browsereinstellungen und versuchen Sie es erneut.");
            }
            throw error;
        }
    }

    async saveDataHandler(worker: () => Promise<any>, componentView?: ComponentView, successToast?: boolean, navigateBack?: boolean, successTitle?: string, successText?: string): Promise<[boolean, any]> {
        try {
            let result = await worker();

            if (successToast) {
                this.showToast("success", successTitle ?? this.t("general.updatedTitle"), successText ?? this.t("general.updatedText"));
            }
            if (navigateBack) {
                if (componentView != null) {
                    componentView.skipCloseCheck = true;
                    await componentView.close();
                }
                else {
                    this.navigateBack();
                }
            }

            return [true, result];
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.dialog.info(!Utils.isNoe(error.items) ? error.items[0].message : "Es trat ein Fehler beim Speichern auf.", "Fehler beim Speichern");
            return [false, null];
        }
    }

    async saveDataHandler2<T>(data: SaveDataHandler<T>): Promise<SaveDataResult<T>> {
        try {
            let result = await data.worker();
            let isSuccess = data.isSuccess(result);

            if (isSuccess) {
                switch (data.successNotificationType) {
                    case UserNotificationType.toast:
                        this.showToast("success", data.successTitle ?? this.t("general.updatedTitle"), data.successText ?? this.t("general.updatedText"));
                        break;
                    case UserNotificationType.dialog:
                        await this.dialog.info(data.successText ?? this.t("general.updatedText"), data.successTitle ?? this.t("general.updatedTitle"));
                        break;
                }

                if (data.navigateBack) {
                    if (data.componentView != null) {
                        data.componentView.skipCloseCheck = true;
                        await data.componentView.close();
                    }
                    else {
                        this.navigateBack();
                    }
                }
                else if (data.navigateTo != null) {
                    if (data.componentView != null) {
                        data.componentView.skipCloseCheck = true;
                    }
                    await this.navigateTo(data.navigateTo);
                }
            }

            return new SaveDataResult(true, result);
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            switch (data.errorNotificationType) {
                case UserNotificationType.toast:
                    this.showToast("error", data.errorTitle ?? this.t("general.errorTitle"), !Utils.isNoe(error.items) ? error.items[0].message : data.errorText ?? this.t("general.errorText"));
                    break;
                case UserNotificationType.dialog:
                    await this.dialog.info(!Utils.isNoe(error.items) ? error.items[0].message : data.errorText ?? this.t("general.errorText"), data.errorTitle ?? this.t("general.errorTitle"));
                    break;
            }
            return new SaveDataResult(false, null);
        }
    }

    async deleteDataHandler<T>(worker: () => Promise<T>, componentView?: ComponentView, successToast?: boolean, navigateBack?: boolean, successTitle?: string, successText?: string): Promise<[boolean, T]> {
        try {
            let result = await worker();

            if (successToast) {
                this.showToast("success", successTitle ?? this.t("general.deletedTitle"), successText ?? this.t("general.deletedText"));
            }
            if (navigateBack) {
                if (componentView != null) {
                    componentView.skipCloseCheck = true;
                }
                this.navigateBack();
            }

            return [true, result];
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.dialog.info(!Utils.isNoe(error.items) ? error.items[0].message : "Es trat ein Fehler beim Löschen auf.", "Fehler beim Löschen");
            return [false, null];
        }
    }

    updateSuccess(): void {
        this.showToast("success", this.t("general.updatedTitle"), this.t("general.updatedText"));
        this.navigateBack();
    }

    public getTranslationMap(): OrdinaryObject {
        return this.translation;
    }

    public t(key: string, params?: OrdinaryObject, def?: string, warnIfMissing = true): string {
        let result = Objects.getPropertyNested(this.translation, key, true);
        if (result == ObjectsInternal.PROPERTY_NOT_FOUND) {
            warnIfMissing ??= this.multilingual;
            if (warnIfMissing) {
                console.warn("Übersetzung für Schlüssel " + key + " nicht gefunden");
            }
            return def ?? "";
        }

        if(params && typeof result === 'string') {
            const template = Utils.getTemplateFunction(result);

            return template(params);
        }
        return result;
    }

    public tRegex(key: string, value: string): string {
        let result = Objects.getPropertyNested(this.translation, key);
        if (Arrays.is(result)) {
            for (let i = 0; i < result.length; i += 2) {
                value = Strings.replaceAll(value, (result as string[])[i], (result as string[])[i + 1]);
            }
        }
        return value;
    }

    public tRegexHas(key: string): boolean {
        return Arrays.is(Objects.getPropertyNested(this.translation, key));
    }

    public tCrudTitle(isNew: boolean, item: any, entityPath: string): string {
        let gender = this.t(entityPath + ".typeNameGender") ?? "m";
        gender = gender == "f" ? "e" : gender == "n" ? "es" : "er";
        return isNew ? this.t("crud.new") + gender + " " + this.t(entityPath + ".typeName") : this.t(entityPath + ".typeName") + " " + Objects.toString(item);
    }

    public tForm(entityPath: string, form: FrontendForm): FrontendForm {
        for (let field of form.fields) {
            if (Utils.isNoe(field.caption)) {
                field.caption = this.t(entityPath + "." + field.id);
            }
        }
        return form;
    }

    public tTable(entityPath: string, table: TableData): TableData {
        for (let col of table.columns) {
            if (Utils.isNoe(col.caption)) {
                col.caption = this.t(entityPath + "." + col.id);
            }
        }
        return table;
    }

    public getRouteParam(paramName: string): any {
        return this.getRouteParamInternal(this.router.routerState.snapshot.root, paramName);
    }

    private getRouteParamInternal(snapshot: ActivatedRouteSnapshot, paramName: string): any {
        if (Objects.hasProperty(snapshot.params, paramName)) {
            return snapshot.params[paramName];
        }
        else {
            for (let child of snapshot.children) {
                let result = this.getRouteParamInternal(child, paramName);
                if (result != null) {
                    return result;
                }
            }
        }
        return null;
    }

    private getRouterDebugStringInternal(parent: string, config: Route[], level = 0): string {
        let result = "";
        let prefix = " ".repeat(level * 2);
        for (let route of config) {
            result += prefix + parent + '/' + route.path + "\n";
            if (route.children) {
                const currentPath = route.path ? parent + '/' + route.path : parent;
                result += prefix + this.getRouterDebugStringInternal(currentPath, route.children, level + 1) + "\n";
            }
        }
        return result;
    }

    public isMobile(): boolean {
        return this._isMobile;
    }

    openContextMenu(menu: MenuItem[], styleClass?: string): void {
        this.contextMenu = Arrays.withoutNull(menu);
        this.contextMenuClass = styleClass ?? "";
        GuiUtils.angularTimer(() => {
            AppMainComponent.instance.cm.show(this.getCurrentMousePosEvent());
        });
    }

    setCurrentMousePos(event: {pageX: number, pageY: number}): void {
        this.mouseX = event.pageX;
        this.mouseY = event.pageY;
    }

    getCurrentMousePosEvent(): OrdinaryObject {
        let result: OrdinaryObject = {};
        result.pageX = this.mouseX;
        result.pageY = this.mouseY;
        result.stopPropagation = () => null;
        result.preventDefault = () => null;
        return result;
    }

    setMainConfig(value: MainFrontendConfig): void {
        this.mainConfig = value ?? new MainFrontendConfig();
        if (Utils.isNoe(this.mainConfig.allowedLanguages)) {
            this.mainConfig.allowedLanguages = this.mainConfig.multilingual ? ["de", "en", "fr", "es"] : ["de"];
        }
        if (!this.mainConfig.allowedLanguages.includes(this.language)) {
            this.language = this.mainConfig.allowedLanguages[0];
            localStorage.setItem("language", this.language);
        }
        this.auth.anonymousOnly = this.mainConfig.authRules.anonymousOnly;
        this.auth.setHttpOnlyAuthenticationCookieAvailable(this.mainConfig.httpOnlyAuthenticationCookieAvailable);
        this.toolbarShowUser = !this.auth.anonymousOnly;
        if (this.mainConfig.csvToExcelAvailable) {
            GuiUtilsInternal.convertCsvToExcelFunction = async (csv, columns) => RestEndpoint.main().body({ text: csv, columns }).post().run("api/app/csvToExcel").getString();
        }
        if (this.mainConfig.activeModules != null) {
            Config.get().activeModules = this.mainConfig.activeModules;
        }
        Config.get().configuredModules = this.mainConfig.configuredModules ?? this.mainConfig.activeModules;
        if (Utils.isNoe(this.mainConfig.appName)) {
            throw new Error("Fehler in der Konfigurationsdatei: Feld 'appName' wurde nicht angegeben");
        }
        this.appName = this.mainConfig.appName;
        this.updatePageTitle(this.appName);
        this.showBreadcrumbs = this.mainConfig.showBreadcrumbs;
        this.breadcrumbsInToolBar = this.mainConfig.breadcrumbsInToolBar;
        this.showSidebar = this.mainConfig.showSidebar;
        this.toolbarBackButton = this.mainConfig.showBackButton;
        this.toolbarHelpButton = this.mainConfig.showHelpButton;
        this.staticHeaderHtml = this.mainConfig.staticHeaderHtml;
        this.privacyPolicy = this.mainConfig.privacyPolicy;
        this.developer = this.mainConfig.developer;
        this.setDeveloperErrorCapture(this.developer);
        this.multilingual = this.mainConfig.multilingual;
        this.warningOnSwitchLanguage = this.mainConfig.warningOnSwitchLanguage;
        this.allowDarkMode = this.mainConfig.allowDarkMode;
        this.reportErrorsToBackend = this.mainConfig.reportErrorsToBackend;
        this.setCompactGui(this.mainConfig.compactGui);
        this.updateDarkMode();
    }

    private setDeveloperErrorCapture(value: boolean): void {
        if (value && !this.developerErrorCaptureEnabled) {
            this.developerErrorCaptureEnabled = true;
            SmErrorHandler.instance.onError.push(error => this.storedErrors.push(error));
        }
    }

    setCompactGui(value: boolean): void {
        this.compactGui = value;
        //Die Klasse muss auf diesem Wege direkt auf dem Body gesetzt werden, weil andernfalls Komponenten,
        //die nicht innerhalb des MainComponent liegen nicht korrekt gestyled werden, z.B. Dialoge von Optimus UI
        if (this.compactGui) {
            document.body.classList.add('compact');
        }
    }

    setBeta(value: boolean): void {
        this.isBeta = value;
    }

    lastChangeId: number = null;
    onChanges = new Listener<ChangeSummary>();
    onIsMobileChanged = new Listener<boolean>();
    trackChangesTimer: SmTimer;

    async initChangeTracking(): Promise<void> {
        this.trackChangesTimer ??= new SmTimer("trackChanges", 60 * 1_000, 60 * 1_000, this.trackChanges.bind(this));
        this.lastChangeId = await RestEndpoint.main().run("api/app/changeSummary").getNumber();
    }

    private trackChangesPromise: Promise<void> | null = null;
    private trackChangesRequested = false;

    async trackChanges(): Promise<void>
    {
        if (this.trackChangesTimer == null) {
            return;
        }

        this.trackChangesRequested = true;
        if (this.trackChangesPromise == null)
        {
            this.trackChangesPromise = this.runPendingTrackChanges();
        }

        await this.trackChangesPromise;
    }

    private async runPendingTrackChanges(): Promise<void>
    {
        try
        {
            while (this.trackChangesRequested)
            {
                this.trackChangesRequested = false;
                await this.trackChangesInternal();
            }
        }
        finally
        {
            this.trackChangesPromise = null;
        }
    }

    async trackChangesInternal(): Promise<void> {
        let summary = await RestEndpoint.main().query({ lastChangeId: this.lastChangeId }).run("api/app/changeSummary").get(ChangeSummary);
        this.lastChangeId = summary.lastId;

        if (!Utils.isNoe(summary.changes)) {
            await this.onChanges.emit(summary);
      /*      for (let change of summary.changes) {
                this.showInfoToast("Änderung: " + change.entityType);
            }
       */ }
    }

    registerChangesCallback(callback: ListenerCallback<ChangeSummary> | ListenerCallbackInfo<ChangeSummary>, title?: string, priority = 0): Subscription {
        return this.onChanges.subscribe(callback, title, priority);
    }

    private getCacheKey(entity: SmBaseEntity): string {
        return `u${this.auth.getUserId()}:${Objects.getTypeName(entity)}:${entity.id}`;
    }

    cacheEntity(entity: SmBaseEntity): void {
        localStorage.setItem(this.getCacheKey(entity), Objects.toJson(entity));
    }

    async loadEntity<T extends SmBaseEntity>(type: ClassType<T>, fallback: T): Promise<T> {
        let base: Partial<T> = fallback;

        const key = this.getCacheKey(fallback);

        const item = localStorage.getItem(key);
        if(item) {
            try {
                const parsedItem = JSON.parse(item) as Partial<T>;

                if(parsedItem.id === fallback.id && await this.dialog.yesNo(this.t("general.cachedItem.message"), this.t("general.cachedItem.title"))) {
                    base = parsedItem;
                }
                else {
                    localStorage.removeItem(key);
                }
            }
            catch {
                localStorage.removeItem(key);
            }
        }

        return Objects.fromPlain(type, base);
    }

    removeEntity(entity: SmBaseEntity): void {
        localStorage.removeItem(this.getCacheKey(entity));
    }
}
